import { Boss, type BossDef } from './boss-base';
import { getTexture } from '@/assets/resolver';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';

/**
 * B10 — Null Crown (void Elemental). Its signature is the
 * EVENT HORIZON: a periodic singularity shield that SWALLOWS the player's
 * bullets (no damage gets through) and, when it collapses, SPITS them back
 * as a void volley. Between shields it BLINKS — teleporting across the
 * arena so it never sits still. Damage only lands while the horizon is open.
 * docs/02 Elementals.
 * Signatures: event horizon + spit-back + blink (always), void ring (P1/P2),
 * void spiral (P3).
 */
export const NULL_CROWN: BossDef = {
  id: 'null',
  nameKey: 'boss.null',
  tier: 4,
  minRoom: 3,
  baseHp: 1550,
  radius: 46,
  bodyKey: 'boss/null/body',
  movement: 'orbit',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/void', count: 10, interval: 2.6,
          spinDeg: 13, speed: 120, radius: 10, damage: 1,
        },
      ],
    },
    {
      hpPct: 0.62,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/void', count: 12, interval: 2.4,
          spinDeg: -15, speed: 130, radius: 10, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/void', count: 3, spreadDeg: 18,
          interval: 2.8, telegraph: 0.4, speed: 250, radius: 10, damage: 1,
          unlockTier: 1,
        },
      ],
    },
    {
      hpPct: 0.3,
      speedMul: 1.3,
      patterns: [
        {
          type: 'spiral', bullet: 'bullet/enemy/void', arms: 3, interval: 0.12,
          angularSpeed: 90, speed: 150, radius: 9, damage: 1,
        },
        {
          type: 'radial', bullet: 'bullet/enemy/ring', count: 10, interval: 3.4,
          spinDeg: 18, speed: 110, radius: 10, damage: 2, unlockTier: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Null lance: a telegraphed void fan out of the crown
      tier: 2,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/void', count: 3, spreadDeg: 22,
        interval: 3.6, telegraph: 0.45, speed: 245, radius: 10, damage: 1,
      },
    },
    {
      // Accretion: a lazy twin-arm void spiral once the crown cracks
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'spiral', bullet: 'bullet/enemy/void', arms: 2, interval: 0.24,
        angularSpeed: 55, speed: 120, radius: 9, damage: 1,
      },
    },
  ],
};

const OPEN_TIME = 4.2; // vulnerable window
const SHIELD_TIME = 3.4; // event horizon up — swallows bullets
const SPIT_SPEED = 200;
const SPIT_CAP = 26; // most bullets it can hurl back at once
const TP_RANGE_X = 320;
const TP_RANGE_Y = 180;

export class NullBoss extends Boss {
  private shielded = false;
  private cycle = OPEN_TIME;
  private swallowed = 0;
  private warp = 0; // blink fade timer
  private bob = 0;
  private tpAngle = 1.7;
  private cx: number;
  private cy: number;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(NULL_CROWN, level, x, y, homeX, homeY);
    this.cx = homeX;
    this.cy = homeY;
    this.allowReinforce = false; // its own damage gate is the shield
  }

  override get isVulnerable(): boolean {
    return super.isVulnerable && !this.shielded;
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;
    if (this.warp > 0) this.warp -= dt;

    this.cycle -= dt;
    if (this.cycle <= 0) {
      if (this.shielded) {
        // Horizon collapses — everything it ate comes screaming back out
        this.shielded = false;
        this.cycle = OPEN_TIME;
        this.spitBack(bullets);
      } else {
        // Raise the horizon and blink to a fresh spot
        this.shielded = true;
        this.cycle = SHIELD_TIME / this.levelSpeedMul;
        this.swallowed = 0;
        this.teleport();
      }
    }
  }

  /** Gentle drift around a center that the blink relocates. */
  protected override move(dt: number, px: number, py: number): void {
    void px; void py;
    this.bob += dt;
    const tx = this.cx + Math.cos(this.bob * 0.8) * 36;
    const ty = this.cy + Math.sin(this.bob * 1.1) * 28;
    const k = Math.min(1, 3 * dt);
    this.x += (tx - this.x) * k;
    this.y += (ty - this.y) * k;
  }

  private teleport(): void {
    this.tpAngle += 2.39996; // golden angle — varied, deterministic
    this.cx = this.homeX + Math.cos(this.tpAngle) * TP_RANGE_X;
    this.cy = this.homeY + Math.sin(this.tpAngle * 1.3) * TP_RANGE_Y;
    this.x = this.prevX = this.cx;
    this.y = this.prevY = this.cy;
    this.warp = 0.3;
  }

  private spitBack(bullets: BulletSystem): void {
    const n = Math.min(this.swallowed, SPIT_CAP);
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / Math.max(1, n);
      bullets.spawn({
        x: this.x, y: this.y, dirX: Math.cos(a), dirY: Math.sin(a),
        speed: SPIT_SPEED * this.bulletSpeedMul, life: 4, radius: 10, damage: 1,
        friendly: false, texKey: 'bullet/enemy/void', rotates: false,
      });
    }
    this.swallowed = 0;
  }

  /** Event horizon eats the player's shots — no damage, counts for the spit. */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    if (this.shielded) {
      this.swallowed += bullets.collideCircle(
        true, this.x, this.y, this.def.radius + 24, () => {},
      );
      return { hits: 0, dealt: 0 };
    }
    return super.absorbBullets(bullets);
  }

  override takeDamage(amount: number): boolean {
    if (this.shielded) return false; // AoE/mortar also bounces off the horizon
    return super.takeDamage(amount);
  }

  override render(alpha: number): void {
    super.render(alpha);
    this.body.texture = getTexture('boss/null/body', this.shielded ? 1 : 0);
    this.body.rotation += (this.shielded ? 0.045 : 0.012);
    // Blink flash — fades back in after a teleport
    this.view.alpha = this.warp > 0 ? 0.35 + (0.3 - this.warp) / 0.3 * 0.65 : 1;
  }
}
